import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
} from "@mui/material";
import { T } from "theme/tokens";

/**
 * Confirmation for an action someone will later have to justify.
 *
 * `requireReason` is the point of this component: rejecting a pharmacy or
 * deleting one writes to the audit log, and a log entry with no reason is
 * only marginally better than no entry. The confirm button stays disabled
 * until a reason is typed.
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  requireReason = false,
  reasonLabel = "Reason",
  reasonPlaceholder = "Why are you doing this?",
  onConfirm,
  onClose,
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setSubmitting(false);
      setError("");
    }
  }, [open]);

  const reasonOk = !requireReason || reason.trim().length >= 3;

  const handleConfirm = async () => {
    setError("");
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "That did not work."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: T.ink, pb: 1 }}>
        {title}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {description && (
            <Typography sx={{ fontSize: "0.88rem", color: T.muted }}>
              {description}
            </Typography>
          )}

          {requireReason && (
            <TextField
              autoFocus
              multiline
              minRows={2}
              size="small"
              label={reasonLabel}
              placeholder={reasonPlaceholder}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Recorded in the pharmacy's activity log."
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none", color: T.muted }}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !reasonOk}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            bgcolor: destructive ? T.error : T.primary,
            "&:hover": { bgcolor: destructive ? "#c62828" : "#013838" },
          }}
        >
          {submitting ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
